import { useQuery } from '@apollo/client';
import React from 'react';
import LoadingIndicator from '../../components/LoadingIndicator';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';

const ALL_INVITATION_QUERY = gql(/** @complexity 6 */ `
  query allInvitationsForProvisions {
    session: currentSession {
      organization {
        id
        # eslint-disable-next-line @graphql-eslint/require-selections
        invitations {
          provision {
            id
          }
        }
      }
    }
  }
`);

const PROVISION_DELETION_CONFIRM_MESSAGE_PROVISION_FRAGMENT = gql(`
  fragment ProvisionDeletionConfirmMessage_provision on Provision {
    id
    isSsoDefault
  }
`);

interface BaseProvisionDeletionConfirmMessageProps {
  isSsoDefault: boolean;
  hasInvitations: boolean;
}

// eslint-disable-next-line import/no-unused-modules -- expose for testing
export const BaseProvisionDeletionConfirmMessage = ({
  isSsoDefault,
  hasInvitations,
}: BaseProvisionDeletionConfirmMessageProps) => {
  if (isSsoDefault && hasInvitations) {
    return (
      <>
        Deleting this default provision will also revoke all associated
        invitations. You can choose a new default from the Authentication
        settings.
      </>
    );
  }

  if (isSsoDefault) {
    return (
      <>
        This is the default provision. You can choose a new default from the
        Authentication settings.
      </>
    );
  }

  if (hasInvitations) {
    return (
      <>Deleting this provision will also revoke all associated invitations.</>
    );
  }

  return null;
};

interface ProvisionDeletionConfirmMessageProps {
  provision: DocumentType<
    typeof PROVISION_DELETION_CONFIRM_MESSAGE_PROVISION_FRAGMENT
  >;
}

const ProvisionDeletionConfirmMessage = ({
  provision,
}: ProvisionDeletionConfirmMessageProps) => {
  const { data, loading } = useQuery(ALL_INVITATION_QUERY);

  const allInvitations = data?.session?.organization?.invitations;

  if (loading && !allInvitations) {
    return <LoadingIndicator />;
  }

  const invitations = (allInvitations || []).filter(
    (invitation) => invitation?.provision?.id === provision?.id,
  );

  return (
    <BaseProvisionDeletionConfirmMessage
      isSsoDefault={provision?.isSsoDefault ?? false}
      hasInvitations={invitations.length > 0}
    />
  );
};

export default ProvisionDeletionConfirmMessage;
